/*
 * The vocabulary of the attachment tray. One file so the state machine, the transport and the
 * component agree on what a staged file is without any of them importing the others' internals.
 *
 * A staged file has two lives. Before the bytes are on the server it exists only here, keyed by the
 * `clientUuid` that is also the upload route's idempotency key: retrying, cancelling and resuming
 * all name that uuid and never a row id. After the bytes are stored it also exists as a row the
 * server owns, and from that moment the draft carries it (`AttachmentRef`), so a reload finds it
 * again while the upload itself is long forgotten.
 */

import type { AttachmentRef, TargetKey } from "../../drafts/types.ts"

/**
 * The life of one staged file. `queued` is waiting for one of the three upload slots, `uploading`
 * is on the wire, `ready` means the server holds the bytes and the row, and `failed` is the one
 * state a person has to answer — with a retry or with the ✕.
 */
export type StagedState = "queued" | "uploading" | "ready" | "failed"

/** A refusal as the chip shows it: the server's own locale key and the numbers its text names. */
export interface StagedError {
	key: string
	args?: Record<string, string | number>
	/** False for a refusal that a repeat of the same request cannot outlive — every 4xx but 429. */
	retryable: boolean
}

/** One file in the tray, in the order it will be sent. */
export interface StagedFile {
	/** The upload's idempotency key, and this item's identity in the tray for its whole life. */
	uuid: string
	name: string
	bytes: number
	state: StagedState
	/** 0…1 while `uploading`; 1 once the server has the bytes. */
	progress: number
	/** The caption, which is also the alt text of an image. */
	caption: string
	/**
	 * A sentence the chip carries about where the file came from — "Pasted text · 1,240 lines · 1
	 * secret hidden" for a long paste the composer turned into a file. It belongs to this tray only:
	 * the draft does not keep it, and the message does not send it.
	 */
	note?: string
	/** Set from the moment the upload succeeded; what the draft and the send carry. */
	id?: number
	/** The type the server decided from the extension and a sniff — never the browser's guess. */
	mime?: string
	kind?: string
	thumbUrl?: string
	width?: number
	height?: number
	error?: StagedError
}

/** What a tray hands the draft: the files whose bytes the server already holds, in tray order. */
export function readyRefs(files: readonly StagedFile[]): AttachmentRef[] {
	const refs: AttachmentRef[] = []
	for (const file of files) {
		if (file.state !== "ready" || file.id === undefined) continue
		const ref: AttachmentRef = {
			id: file.id,
			name: file.name,
			bytes: file.bytes,
			contentType: file.mime ?? "application/octet-stream",
		}
		if (file.caption !== "") ref.caption = file.caption
		if (file.kind !== undefined) ref.kind = file.kind
		if (file.thumbUrl !== undefined) ref.thumbUrl = file.thumbUrl
		refs.push(ref)
	}
	return refs
}

/** The tray a draft is restored into: every reference is a file the server already holds. */
export function stagedFromRefs(refs: readonly AttachmentRef[]): StagedFile[] {
	return refs.map((ref) => {
		const file: StagedFile = {
			// A restored reference has no upload left to name, so the row id is its identity.
			uuid: `a:${ref.id}`,
			name: ref.name,
			bytes: ref.bytes,
			state: "ready",
			progress: 1,
			caption: ref.caption ?? "",
			id: ref.id,
			mime: ref.contentType,
		}
		if (ref.kind !== undefined) file.kind = ref.kind
		if (ref.thumbUrl !== undefined) file.thumbUrl = ref.thumbUrl
		return file
	})
}

/**
 * The upload scope of a composer, which is its draft's target key verbatim (`c:<id>`,
 * `c:<id>:t:<root>`, `e:<messageId>`). The server parses the same three shapes, so the tray never
 * builds a second spelling of where a file belongs.
 */
export type UploadScope = TargetKey
